import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Calendar as CalendarIcon, X as ClearIcon } from 'lucide-react';
import { DayPicker, type DateRange } from 'react-day-picker';
import { addDays, endOfWeek, format, getISOWeek, getISOWeekYear, parseISO, setISOWeek, setISOWeekYear, startOfWeek, subDays } from 'date-fns';
import { cn } from '@/lib/cn';
import { Input } from './Input';

type IsoDate = string; // YYYY-MM-DD

function toIsoDate(d: Date): IsoDate {
  return format(d, 'yyyy-MM-dd');
}

function parseIsoDate(value: string | null | undefined): Date | undefined {
  if (!value) return undefined;
  try {
    return parseISO(value);
  } catch {
    return undefined;
  }
}

function useClickOutside(ref: React.RefObject<HTMLElement | null>, onOutside: () => void) {
  useEffect(() => {
    function onPointerDown(event: MouseEvent) {
      const el = ref.current;
      if (!el) return;
      if (event.target instanceof Node && el.contains(event.target)) return;
      onOutside();
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [ref, onOutside]);
}

const dayPickerClassNames = {
  root: 'rdp-root rdp-tailadmin relative w-fit',
  // caption + nav: TailAdmin-like (arrows + month label on one line)
  months: 'rdp-months w-fit',
  month: 'rdp-month w-fit',
  month_caption: 'rdp-month_caption flex h-10 items-center justify-center',
  caption_label: 'rdp-caption_label text-sm font-semibold text-gray-900 dark:text-white',
  nav: 'rdp-nav',
  button_previous:
    'rdp-button_previous inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 shadow-xs hover:bg-gray-50 dark:border-white/10 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800',
  button_next:
    'rdp-button_next inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-700 shadow-xs hover:bg-gray-50 dark:border-white/10 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800',

  month_grid: 'rdp-month_grid mt-2 w-auto border-collapse',
  weekdays: 'rdp-weekdays font-medium text-gray-500 dark:text-gray-400',
  weekday: 'rdp-weekday px-1 pb-1.5 pt-1 text-center',
  week_number: 'rdp-week_number font-medium text-gray-500 dark:text-gray-400',
  week_number_header: 'rdp-week_number_header font-medium text-gray-500 dark:text-gray-400',
  weeks: 'rdp-weeks',
  week: 'rdp-week',

  day: 'rdp-day p-0 text-center',
  day_button:
    'rdp-day_button inline-flex h-8 w-8 items-center justify-center text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800',

  selected: 'rdp-selected',
  today: 'rdp-today',
  range_start: 'rdp-range_start',
  range_end: 'rdp-range_end',
  range_middle: 'rdp-range_middle',
  outside: 'rdp-outside',
  disabled: 'rdp-disabled [&>button]:text-gray-300 [&>button]:opacity-40 dark:[&>button]:text-gray-700',
};

function popoverContainerClassName(className?: string) {
  return cn(
    'z-50 w-fit max-w-[calc(100vw-2rem)] rounded-xl border border-gray-200 bg-white p-3 shadow-card',
    'fixed left-1/2 top-24 -translate-x-1/2',
    'md:absolute md:left-auto md:right-0 md:top-auto md:mt-2 md:max-w-[calc(100vw-2rem)] md:translate-x-0',
    'max-h-[calc(100vh-10rem)] overflow-auto',
    'dark:border-white/10 dark:bg-gray-900 dark:shadow-card-dark',
    className,
  );
}

export interface SingleDatePickerProps {
  value: IsoDate;
  onChange: (next: IsoDate) => void;
  allowedDates?: IsoDate[]; // if set, disable all others
  disabled?: boolean;
  label?: ReactNode;
  ariaLabel?: string;
}

export function SingleDatePicker({
  value,
  onChange,
  allowedDates,
  disabled,
  label,
  ariaLabel,
}: SingleDatePickerProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapRef, () => setOpen(false));

  const selected = useMemo(() => parseIsoDate(value), [value]);
  const allowed = useMemo(() => new Set(allowedDates ?? []), [allowedDates]);
  const disableAllExceptAllowed = allowedDates !== undefined;

  return (
    <div ref={wrapRef} className="relative">
      {label ? (
        <span className="mb-1 block text-xs font-medium text-gray-500 dark:text-gray-400">{label}</span>
      ) : null}
      <button type="button" className="w-full text-left" onClick={() => !disabled && setOpen((v) => !v)}>
        <Input
          value={value}
          readOnly
          disabled={disabled}
          aria-label={ariaLabel}
          rightIcon={<CalendarIcon className="h-4 w-4" aria-hidden="true" />}
        />
      </button>
      {open && !disabled ? (
        <div className={popoverContainerClassName()}>
          <DayPicker
            mode="single"
            selected={selected}
            defaultMonth={selected}
            navLayout="around"
            fixedWeeks
            showOutsideDays
            onSelect={(d) => {
              if (!d) return;
              const next = toIsoDate(d);
              if (disableAllExceptAllowed && !allowed.has(next)) return;
              onChange(next);
              setOpen(false);
            }}
            disabled={(d) => (disableAllExceptAllowed ? !allowed.has(toIsoDate(d)) : false)}
            weekStartsOn={1}
            classNames={dayPickerClassNames}
          />
        </div>
      ) : null}
    </div>
  );
}

export interface RangeValue {
  from: IsoDate;
  to: IsoDate;
}

export interface RangeDatePickerProps {
  value: RangeValue;
  onChange: (next: RangeValue) => void;
  disabled?: boolean;
  ariaLabel?: string;
  /**
   * Makes the range optional: `value` may be `{ from: '', to: '' }` (no range, `placeholder` shown) and, while a
   * range is set, a clear button calls this. Omit it for a range that is always set.
   */
  onClear?: () => void;
  placeholder?: string;
}

export function RangeDatePicker({ value, onChange, disabled, ariaLabel, onClear, placeholder }: RangeDatePickerProps) {
  const [open, setOpen] = useState(false);
  // A first click only has a start; it is kept here until the second click completes the range.
  const [draft, setDraft] = useState<DateRange | undefined>(undefined);
  const wrapRef = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    setOpen(false);
    setDraft(undefined);
  }, []);
  useClickOutside(wrapRef, close);

  const range = useMemo(() => ({ from: parseIsoDate(value.from), to: parseIsoDate(value.to) }), [value.from, value.to]);
  const isSet = value.from !== '' || value.to !== '';
  const display = isSet ? `${value.from} → ${value.to}` : '';

  return (
    <div ref={wrapRef} className="relative">
      <button type="button" className="w-full text-left" onClick={() => !disabled && (open ? close() : setOpen(true))}>
        <Input
          value={display}
          placeholder={placeholder}
          readOnly
          disabled={disabled}
          aria-label={ariaLabel}
          rightIcon={<CalendarIcon className="h-4 w-4" aria-hidden="true" />}
        />
      </button>
      {onClear && isSet && !disabled ? (
        <button
          type="button"
          aria-label="Clear date range"
          className="absolute right-9 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
          onClick={() => {
            close();
            onClear();
          }}
        >
          <ClearIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      ) : null}
      {open && !disabled ? (
        <div className={popoverContainerClassName()}>
          <DayPicker
            mode="range"
            selected={draft ?? range}
            defaultMonth={range.from}
            navLayout="around"
            fixedWeeks
            showOutsideDays
            onSelect={(next) => {
              if (!next?.from || !next?.to) {
                if (next?.from) setDraft({ from: next.from, to: undefined });
                return;
              }
              setDraft(undefined);
              onChange({ from: toIsoDate(next.from), to: toIsoDate(next.to) });
            }}
            weekStartsOn={1}
            classNames={dayPickerClassNames}
          />
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              className="rounded-lg px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-800"
              onClick={close}
            >
              Done
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export interface WeekPickerProps {
  value: { year: number; week: number };
  onChange: (next: { year: number; week: number }) => void;
  availableWeeks: number[]; // for selected year
  disabled?: boolean;
  ariaLabel?: string;
}

// Week picker based on https://daypicker.dev/guides/custom-selections#week-selection
export function WeekPicker({ value, onChange, availableWeeks, disabled, ariaLabel }: WeekPickerProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapRef, () => setOpen(false));

  const available = useMemo(() => new Set(availableWeeks), [availableWeeks]);
  const valueWeekRange = useMemo(() => weekRangeFromIso(value.year, value.week), [value.year, value.week]);
  const displayWeekRange = available.has(value.week) ? valueWeekRange : undefined;

  return (
    <div ref={wrapRef} className="relative">
      <button type="button" className="w-full text-left" onClick={() => !disabled && setOpen((v) => !v)}>
        <Input
          value={`Week ${value.week}`}
          readOnly
          disabled={disabled}
          aria-label={ariaLabel}
          rightIcon={<CalendarIcon className="h-4 w-4" aria-hidden="true" />}
        />
      </button>
      {open && !disabled ? (
        <div className={popoverContainerClassName()}>
          <DayPicker
            showWeekNumber
            showOutsideDays
            fixedWeeks
            defaultMonth={displayWeekRange?.from ?? valueWeekRange.from}
            navLayout="around"
            weekStartsOn={1}
            modifiers={{
              selected: displayWeekRange,
              range_start: displayWeekRange?.from,
              range_end: displayWeekRange?.to,
              range_middle: rangeMiddleOnly(displayWeekRange),
            }}
            disabled={(day) => {
              const wk = isoWeekForDate(day);
              return wk.year !== value.year || !available.has(wk.week);
            }}
            onDayClick={(day, modifiers) => {
              if (modifiers.disabled || modifiers.hidden) return;
              const wk = isoWeekForDate(day);
              if (wk.year !== value.year) return;
              if (!available.has(wk.week)) return;
              onChange({ year: wk.year, week: wk.week });
              setOpen(false);
            }}
            classNames={dayPickerClassNames}
          />
        </div>
      ) : null}
    </div>
  );
}

function isoWeekForDate(date: Date): { year: number; week: number } {
  return { year: getISOWeekYear(date), week: getISOWeek(date) };
}

function weekRangeFromIso(year: number, week: number): { from: Date; to: Date } {
  let d = setISOWeekYear(new Date(), year);
  d = setISOWeek(d, week);
  return {
    from: startOfWeek(d, { weekStartsOn: 1 }),
    to: endOfWeek(d, { weekStartsOn: 1 }),
  };
}

function rangeMiddleOnly(range: { from: Date; to: Date } | undefined): { from: Date; to: Date } | undefined {
  if (!range) return undefined;
  const from = addDays(range.from, 1);
  const to = subDays(range.to, 1);
  if (from > to) return undefined;
  return { from, to };
}

